import React, { useRef, useState } from "react";
import Header from "../components/Header";
import html2pdf from "html2pdf.js";
import {
  ArrowLeft,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { db } from "../services/firebase";
import { ref, push, set, serverTimestamp } from "firebase/database";

const LOGO_URL = "/logo.png";
const WATERMARK_OPACITY = 0.15;

/* --- CSS STYLES --- */
const pageStyles = `:root {
  --primary: #003366;
  --primary-hover: #022b54;
  --border: #d1d5db;
  --muted: #6b7280;
  --success: #059669;
  --bg: #f8fafc;
  --text: #1e293b;
}

body {
  font-family: 'Arial', sans-serif;
  background-color: var(--bg);
  color: var(--text);
  margin: 0;
}

.preview-paper {
  background: white;
  width: 210mm;
  max-width: 100%;
  box-sizing: border-box;
  padding: 10mm;
  padding-top: 35mm; /* Header space */
  margin: 0 auto 40px;
  color: #000;
  position: relative;
  min-height: 297mm; 
  box-shadow: 0 6px 18px rgba(16,24,40,0.12);
  -webkit-print-color-adjust: exact;
}

/* Toolbar */
.toolbar {
  display: flex; gap: 12px; align-items: center; padding: 1rem 1.25rem;
  background: white; border-bottom: 1px solid var(--border);
  position: sticky; top: 0; z-index: 120; margin-bottom: 12px;
}
.btn {
  padding: 0.6rem 1rem; border-radius: 8px; font-weight: 700;
  border: 1px solid transparent; display: inline-flex; align-items: center; gap: 8px; cursor: pointer; background: #fff;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { color: var(--primary); border-color: var(--primary); background: rgba(0,51,102,0.04); }
.btn-success { background: var(--primary); color: white; border-color: var(--primary); }
.btn-ghost { background: transparent; color: var(--primary-hover); border-color: transparent; }
.status-badge { margin-left: 8px; padding: 6px 10px; border-radius: 8px; font-weight: 700; font-size: 13px; display: flex; align-items: center; gap: 6px; }
.status-processing { background: rgba(59,130,246,0.08); color: #0369a1; border: 1px solid rgba(59,130,246,0.12); }
.status-success { background: rgba(16,185,129,0.08); color: var(--success); border: 1px solid rgba(16,185,129,0.12); }
.status-error { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
.center-status { position: absolute; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 8px; pointer-events: none; }
.toolbar .spacer { flex: 1 1 auto; }
.toolbar .meta { font-size: 0.95rem; color: var(--primary-hover); }

/* HEADER (Screen Preview) */
.pdf-header-container {
  position: absolute; top: 0; left: 14mm; right: 10mm; 
  height: 32mm; 
  display: flex; justify-content: space-between; align-items: center; overflow: visible;
}
.pdf-header-container::after {
  content: ""; position: absolute; bottom: 0; left: -7mm; right: 2mm; height: 1.4px; background: #eab308;
}
.header-left img { width: 80px; height: auto; display: block; position: relative; top: 3mm; left: -2mm; }
.header-right {
  text-align: right; font-family: 'Times New Roman', serif; max-width: calc(100% - 110px);
  padding-right: 6mm; box-sizing: border-box; padding-top: 6mm;
}
.header-brand { color: #2563eb; font-size: 18px; font-weight: 900; text-transform: uppercase; margin: 0; letter-spacing: 1px; }
.header-address { font-size: 9px; color: #9ca3af; margin-top: 4px; line-height: 1.3; max-width: 100%; }

/* PDF CONTENT */
.pdf-cover { text-align: center; margin-bottom: 40px; padding: 10px 0 40px 0; }
.main-title { font-size: 26px; text-transform: uppercase; color: #000; margin-bottom: 15px; letter-spacing: 1px; font-weight: bold; }
.project-title { font-size: 20px; color: var(--primary); margin-bottom: 15px; font-weight: 600; text-transform: uppercase; }
.report-date { font-size: 14px; color: #444; font-weight: 500; margin-top: 5px; }

/* Forces a new page */
.pdf-section-start {
  page-break-before: always;
  padding-top: 5px; 
  display: block;
}

.section-header { background-color: #e6f0ff; border-left: 6px solid var(--primary); color: #000; font-size: 15px; margin-top: 25px; margin-bottom: 12px; text-transform: uppercase; font-weight: 700; padding: 6px 12px; font-family: Arial, sans-serif; }
.sub-header { font-weight: 700; color: #000; margin-bottom: 8px; font-size: 13px; margin-top: 15px; font-family: Arial, sans-serif; }
.body-text { font-size: 11px; line-height: 1.5; color: #333; margin-bottom: 8px; text-align: justify; }

.toc-header-bar { background-color: #ccddee; padding: 5px 10px; font-weight: bold; font-size: 13px; color: #000; border: 1px solid #999; text-transform: uppercase; margin-bottom: 15px; }
.toc-container { font-size: 12px; font-family: Arial, sans-serif; color: #000; }
.toc-row { display: flex; align-items: flex-end; margin-bottom: 5px; }
.toc-text { white-space: nowrap; padding-right: 5px; text-transform: uppercase; }
.toc-bold { font-weight: bold; text-decoration: underline; }
.toc-dots { flex-grow: 1; border-bottom: 2px dotted #000; margin-bottom: 4px; }
.toc-page { padding-left: 5px; font-weight: bold; }
.toc-indent { padding-left: 20px; margin-top: 5px; }
.toc-indent .toc-text { text-decoration: underline; font-weight: bold; font-size: 11px; }

.feature-list-numbered { margin-left: 20px; font-family: inherit; color: #333; }
.feature-item { margin-bottom: 5px; font-size: 12px; }

.relevance-matrix { width: 100%; border-collapse: collapse; font-size: 11px; border: 1px solid #000; margin-bottom: 15px; }
.relevance-matrix th { background-color: #222; color: #fff; border: 1px solid #fff; padding: 6px; font-weight: bold; }
.relevance-matrix td { border: 1px solid #000; padding: 6px; vertical-align: middle; }

.keywords-box { width: 100%; border: 1px solid #000; padding: 12px; font-size: 11px; margin-bottom: 15px; }
.keywords-list { list-style-type: disc; padding-left: 20px; margin: 0; }
.keywords-list li { margin-bottom: 6px; font-weight: 500; }

.links-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px; border: 1px solid #000; }
.links-table th { border: 1px solid #000; padding: 6px; background-color: #f0f0f0; font-weight: bold; }
.links-table td { border: 1px solid #000; padding: 6px; vertical-align: top; word-break: break-all; }

/* Patent Details */
.patent-title-box { background-color: #8eaadb; border: 1px solid #666; padding: 6px 10px; margin-bottom: 10px; font-weight: bold; font-size: 12px; color: #000; display: flex; gap: 10px; align-items: baseline; }
.patent-details-table { width: auto; margin-bottom: 12px; font-size: 12px; border-collapse: collapse; }
.patent-details-table td { padding: 2px 15px 2px 0; vertical-align: top; white-space: nowrap; }
.patent-label { font-weight: bold; width: 80px; display: inline-block; }
.patent-subhead { font-weight: bold; font-size: 10px; color: #444; margin-top: 8px; text-transform: uppercase; }
.patent-image-wrapper { text-align: center; margin-top: 12px; margin-bottom: 20px; }
.patent-image-wrapper img { max-width: 90%; max-height: 220px; border: 1px solid #eee; margin-bottom: 10px; display: block; margin-left: auto; margin-right: auto; }

/* Disclaimer & Utilities */
.disclaimer-box { margin-top: 40px; border-top: 1px solid #eab308; padding-top: 12px; }
.disclaimer-text { font-size: 10px; color: #444; line-height: 1.4; text-align: justify; margin: 0; }
.static-note { font-style: italic; font-size: 10px; color: #666; margin: 12px 0; }
.keep-together { page-break-inside: avoid; }

/* Watermark */
.watermark-preview { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-8deg); width: 50%; max-width: 120mm; opacity: 0.06; pointer-events: none; z-index: 1; }
.watermark-preview img { width: 100%; height: auto; filter: grayscale(1); }
`;

const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const dateObj = new Date(year, month - 1, day);
  const dayNum = parseInt(day);
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };
  return `${dayNum}${getOrdinal(dayNum)} ${dateObj.toLocaleString("default", {
    month: "long",
  })} ${year}`;
};

const processImage = (url, opacity = 1.0) => {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.warn("Image load timed out:", url);
      resolve(null);
    }, 2000);

    const img = new Image();
    img.src = url;
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      clearTimeout(timeout);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.globalAlpha = opacity;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(null);
    };
  });
};

// ADDED: onFinish prop
const ViewDownloadPage = ({ data, back, onLogout, onFinish }) => {
  const reportRef = useRef();
  const [saveStatus, setSaveStatus] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const filledPatents = (data.patents || []).filter(
    (p) => p.patentId && p.title && p.link
  );

  const visibleFeatures = (data.features || []).filter(
    (f) => f && f.text && f.text.trim() !== ""
  );

  const visibleKeywords = (data.keywords || []).filter(
    (k) => k && k.text && k.text.trim() !== ""
  );

  // Snapshot and Print features removed per UX decision

  const handleDownloadPDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setSaveStatus({ ok: true, text: "Generating PDF..." });

    if (db) {
      try {
        const entryRef = push(ref(db, "priorArts"));
        const payload = {
          internalProjectId: data.internalProjectId || null,
          title: data.title || null,
          createdBy: data.createdBy || null,
          createdAt: serverTimestamp(),
        };
        set(entryRef, payload).catch(() => {});
      } catch (e) {
        console.warn("Firebase skipped");
      }
    }

    try {
      const headerLogo = await processImage(LOGO_URL, 1.0);
      const watermarkLogo = await processImage(LOGO_URL, WATERMARK_OPACITY);

      const element = reportRef.current;
      const safeTitle = data.title
        ? data.title.replace(/[^a-z0-9]/gi, "_").substring(0, 20)
        : "Draft";

      const opt = {
        margin: [35, 10, 10, 10],
        filename: `Prior_Art_Report_${safeTitle}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      await html2pdf()
        .set(opt)
        .from(element)
        .toPdf()
        .get("pdf")
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const wmWidth = 120;
          const wmHeight = 120;
          const wmX = (pageWidth - wmWidth) / 2;
          const wmY = (pageHeight - wmHeight) / 2;

          const logoSize = 22;
          const logoX = 10;
          const logoY = 6;
          const rightMargin = 200;
          const textY = 12;
          const lineY = 32;

          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            if (watermarkLogo) {
              try {
                pdf.addImage(
                  watermarkLogo,
                  "PNG",
                  wmX,
                  wmY,
                  wmWidth,
                  wmHeight,
                  undefined,
                  "FAST"
                );
              } catch (e) {}
            }

            if (headerLogo) {
              try {
                pdf.addImage(
                  headerLogo,
                  "PNG",
                  logoX,
                  logoY,
                  logoSize,
                  logoSize,
                  undefined,
                  "FAST"
                );
              } catch (e) {}
            }

            pdf.setFont("times", "bold");
            pdf.setFontSize(21);
            pdf.setTextColor(37, 99, 235);
            pdf.text("LEXTRIA RESEARCH", rightMargin, textY, {
              align: "right",
            });

            pdf.setFont("times", "normal");
            pdf.setFontSize(9);
            pdf.setTextColor(156, 163, 175);
            const addressLines = [
              "#20 & #21, An Astraea Innovative Solutions, 4th floor,",
              "MUTBI, Advanced Research Centre,",
              "Manipal ~ 576104",
            ];
            let addrY = textY + 5;
            addressLines.forEach((line) => {
              pdf.text(line, rightMargin, addrY, { align: "right" });
              addrY += 4;
            });

            pdf.setDrawColor(234, 179, 8);
            pdf.setLineWidth(0.5);
            pdf.line(10, lineY, 200, lineY);
          }
        })
        .save();

      setSaveStatus({ ok: true, text: "PDF Downloaded" });

      // RESTORED: Trigger onFinish after a small delay
      setTimeout(() => {
        setSaveStatus(null);
        if (onFinish) onFinish();
      }, 2000);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      setSaveStatus({ ok: false, text: "Download Failed" });
      alert("Could not generate PDF. Please check console.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <style>{pageStyles}</style>
      <Header active="download" onLogout={onLogout} />
      <div className="page-container fade-in">
        <div className="toolbar">
          <button
            className="btn btn-secondary"
            onClick={back}
            disabled={isGenerating}
          >
            <ArrowLeft size={18} /> Edit Data
          </button>
          {/* Snapshot and Print buttons removed */}
          <div className="center-status">
            {saveStatus && (
              <div
                className={`status-badge ${
                  saveStatus.ok ? "status-success" : "status-error"
                }`}
              >
                {saveStatus.ok ? (
                  <CheckCircle size={14} />
                ) : (
                  <AlertCircle size={14} />
                )}
                {saveStatus.text}
              </div>
            )}
          </div>
          <div className="spacer" />
          <div className="meta">{filledPatents.length} references</div>
          <button
            className="btn btn-success"
            onClick={handleDownloadPDF}
            disabled={isGenerating || !(data.title && filledPatents.length)}
          >
            {isGenerating ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Generating...
              </>
            ) : (
              <>
                <Download size={18} /> Download PDF
              </>
            )}
          </button>
        </div>

        <div className="preview-paper" ref={reportRef}>
          {/* Header for React Preview */}
          <div className="pdf-header-container" data-html2canvas-ignore="true">
            <div className="header-left">
              <img
                src={LOGO_URL}
                alt="Lextria"
                onError={(e) => (e.target.style.display = "none")}
              />
            </div>
            <div className="header-right">
              <h1 className="header-brand">LEXTRIA RESEARCH</h1>
              <div className="header-address">
                #20 & #21, An Astraea Innovative Solutions, 4th floor,
                <br />
                MUTBI, Advanced Research Centre,
                <br />
                Manipal ~ 576104
              </div>
            </div>
          </div>

          <div className="watermark-preview" data-html2canvas-ignore="true">
            <img
              src={LOGO_URL}
              alt="Watermark"
              onError={(e) => (e.target.style.display = "none")}
            />
          </div>

          {/* PAGE 1: COVER */}
          <div className="pdf-cover">
            <h1 className="main-title">Prior art Search Report</h1>
            <h2 className="project-title">{data.title || "[Project Title]"}</h2>
            {data.reportDate && (
              <div className="report-date">{formatDate(data.reportDate)}</div>
            )}
          </div>

          {/* PAGE 2: TABLE OF CONTENTS */}
          <div className="pdf-section-start">
            <div className="toc-header-bar">TABLE OF CONTENTS</div>
            <div className="toc-container">
              <div className="toc-row">
                <span className="toc-text toc-bold">
                  SECTION 1A - INTRODUCTION
                </span>
                <span className="toc-dots"></span>
                <span className="toc-page">3</span>
              </div>
              <div className="toc-row">
                <span className="toc-text toc-bold">
                  SECTION 1B - KEY FEATURES OF THE INVENTION
                </span>
                <span className="toc-dots"></span>
                <span className="toc-page">3</span>
              </div>
              <div className="toc-row">
                <span className="toc-text toc-bold">
                  SECTION 2A - SEARCH RESULTS
                </span>
                <span className="toc-dots"></span>
                <span className="toc-page">4</span>
              </div>
              {filledPatents.map((p, idx) => (
                <div key={p.id} className="toc-row toc-indent">
                  <span className="toc-text toc-bold">
                    {idx + 1}. {p.patentId} {p.title.toUpperCase()}
                  </span>
                  <span className="toc-dots"></span>
                  <span className="toc-page">{4 + idx}</span>
                </div>
              ))}
            </div>
          </div>

          {/* PAGE 3: CONTENT */}
          <div className="pdf-section-start keep-together">
            <h3 className="section-header">SECTION 1A - Introduction</h3>
            <div
              className="body-text"
              dangerouslySetInnerHTML={{
                __html: data.introduction || "<p>No introduction provided.</p>",
              }}
            />
          </div>

          <div className="keep-together">
            <h3 className="section-header">
              SECTION 1B – KEY FEATURES OF THE INVENTION
            </h3>
            <p className="body-text">
              {data.titlePrefix || "A"} {data.title} system, comprising:
            </p>
            <div className="feature-list-numbered">
              {visibleFeatures.map((f, i) => (
                <div key={f.id} className="feature-item">
                  <strong>{i + 1})</strong> {f.text}
                </div>
              ))}
            </div>
          </div>

          <div className="keep-together">
            <h4 className="sub-header">Relevant References:</h4>
            <table className="relevance-matrix">
              <thead>
                <tr>
                  <th style={{ textAlign: "left", paddingLeft: "10px" }}>
                    Patents
                  </th>
                  {visibleFeatures.map((f, i) => (
                    <th
                      key={f.id}
                      style={{ textAlign: "center", width: "40px" }}
                    >
                      {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filledPatents.map((p, idx) => (
                  <tr key={p.id}>
                    <td
                      style={{
                        textAlign: "left",
                        fontWeight: "bold",
                        paddingLeft: "10px",
                      }}
                    >
                      {idx + 1}. {p.patentId}
                    </td>
                    {visibleFeatures.map((f) => (
                      <td key={f.id} style={{ textAlign: "center" }}>
                        {(p.selectedFeatures || []).includes(f.id) ? "√" : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="keep-together">
            <h4 className="sub-header">Keywords (with variations):</h4>
            <div className="keywords-box">
              <ul className="keywords-list">
                {visibleKeywords.length === 0 ? (
                  <li>No keywords defined</li>
                ) : (
                  visibleKeywords.map((k) => <li key={k.id}>{k.text}</li>)
                )}
              </ul>
            </div>
          </div>

          <div className="keep-together" style={{ marginTop: "10px" }}>
            <p className="static-note">
              In addition to manual and keyword searching in the subclasses
              above, keyword searching was conducted with no class limitation.
            </p>
          </div>

          {/* PAGE 4+: RESULTS */}
          <div className="pdf-section-start">
            <h3 className="section-header" style={{ marginBottom: "0px" }}>
              SECTION 2A – SEARCH RESULTS
            </h3>
            <div
              className="section-header"
              style={{ marginTop: "0px", borderTop: "none", fontSize: "14px" }}
            >
              RESULTS OF THE SEARCH - RELEVANT
            </div>
            {filledPatents.map((p, idx) => (
              <div
                key={p.id}
                className="pdf-patent-block keep-together"
                style={{ marginTop: "20px" }}
              >
                <div className="patent-title-box">
                  <span style={{ marginRight: "8px" }}>{idx + 1}.</span>
                  <span style={{ marginRight: "8px" }}>{p.patentId}</span>
                  <span style={{ textTransform: "uppercase" }}>{p.title}</span>
                </div>

                <div style={{ marginLeft: "20px", marginBottom: "8px" }}>
                  <table className="patent-details-table">
                    <tbody>
                      {p.filedDate && (
                        <tr>
                          <td className="patent-label">Filed date</td>
                          <td>: {p.filedDate}</td>
                        </tr>
                      )}
                      {p.inventor && (
                        <tr>
                          <td className="patent-label">Inventor</td>
                          <td>: {p.inventor}</td>
                        </tr>
                      )}
                      {p.assignee && (
                        <tr>
                          <td className="patent-label">Assignee</td>
                          <td>: {p.assignee}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="patent-content">
                  <div className="patent-subhead">[ ABSTRACT ]</div>
                  <div
                    className="body-text"
                    dangerouslySetInnerHTML={{ __html: p.abstract }}
                  />

                  <div className="patent-subhead">[ DETAILED DESCRIPTION ]</div>
                  <div
                    className="body-text"
                    dangerouslySetInnerHTML={{ __html: p.description }}
                  />

                  {/* MULTIPLE IMAGES RENDERED HERE */}
                  {(p.images && p.images.length > 0
                    ? p.images
                    : p.image
                    ? [p.image]
                    : []
                  ).map((imgSrc, imgIdx) => (
                    <div key={imgIdx} className="patent-image-wrapper">
                      <img src={imgSrc} alt={`Patent Figure ${imgIdx + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="keep-together">
            <table className="links-table">
              <thead>
                <tr>
                  <th style={{ width: "60px", textAlign: "left" }}>S.No.</th>
                  <th style={{ textAlign: "left" }}>Link</th>
                </tr>
              </thead>
              <tbody>
                {filledPatents.map((p, idx) => (
                  <tr key={p.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <a
                        href={p.link}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "black", textDecoration: "none" }}
                      >
                        {p.link}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="keep-together disclaimer-box">
            <h3 className="section-header">Disclaimer</h3>
            <p className="disclaimer-text">
              No legal or statutory compliance advice has been sought. The
              report and opinion is based on the technical feasibility and
              practical applications. Only a technical comparison of features
              with the searched prior art are discussed. The prior art has been
              performed on third party databases including USPTO, EPO, Delphion,
              IEEE, ACM and Google patents and the authenticity, accuracy, and
              validity of information of the results dependent on the accuracy
              of information listed in these databases. The search does not
              include results filed before 1976 in USPTO as they are not
              available in digital form. Furthermore, the performed search does
              not include results corresponding to patent application filed in
              USPTO, EPO and others patent office’s and that not been published;
              these patent applicants are not in public domain.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewDownloadPage;
